import React from 'react'
import { Button, Heading, Text } from '../ui'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'
import type { SupportCenterAppointmentCancelChannel } from '../../types/supportCenterAppointments'
import { SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS } from '../../types/supportCenterAppointments'

type AgendaCancelModalProps = {
  patientLabel: string
  cancelReasonId: string
  cancelReasons: SupportCenterCancelReason[]
  loadingCancelReasons: boolean
  cancelling: boolean
  cancelError: string | null
  onReasonChange: (reasonId: string) => void
  cancellationChannel: '' | SupportCenterAppointmentCancelChannel
  onCancellationChannelChange: (channel: '' | SupportCenterAppointmentCancelChannel) => void
  onClose: () => void
  onConfirm: () => void
  /** Título do modal (padrão: cancelar). */
  title?: string
  /** Texto introdutório; use `{patient}` para o nome. */
  description?: string
  reasonLabel?: string
  confirmLabel?: string
  confirmingLabel?: string
  pendingMessage?: string
  /** Classe do overlay (ex.: z-[60] quando empilhado sobre outro modal). */
  overlayClassName?: string
}

export const AgendaCancelModal: React.FC<AgendaCancelModalProps> = ({
  patientLabel,
  cancelReasonId,
  cancelReasons,
  loadingCancelReasons,
  cancelling,
  cancelError,
  onReasonChange,
  cancellationChannel,
  onCancellationChannelChange,
  onClose,
  onConfirm,
  title = 'Cancelar agendamento',
  description = 'Você está cancelando o atendimento de {patient}. O registro permanece no banco (exclusão lógica).',
  reasonLabel = 'Motivo do cancelamento',
  confirmLabel = 'Confirmar cancelamento',
  confirmingLabel = 'Cancelando...',
  pendingMessage = 'Confirmando o cancelamento, aguarde...',
  overlayClassName = 'z-50',
}) => {
  const descriptionNodes = description.split('{patient}').map((part, index, arr) => (
    <React.Fragment key={index}>
      {part}
      {index < arr.length - 1 ? (
        <strong className="text-gray-900 dark:text-white">{patientLabel}</strong>
      ) : null}
    </React.Fragment>
  ))

  return (
    <div className={`fixed inset-0 ${overlayClassName} flex items-center justify-center bg-black/50 p-4`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
          {title}
        </Heading>
        <Text size="sm" color="muted" className="mb-4">
          {descriptionNodes}
        </Text>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Canal do cancelamento <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full mb-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
          value={cancellationChannel}
          disabled={cancelling}
          onChange={(e) =>
            onCancellationChannelChange(
              e.target.value as '' | SupportCenterAppointmentCancelChannel
            )
          }
        >
          <option value="">Selecione...</option>
          {SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {reasonLabel} <span className="text-red-500">*</span>
        </label>
        <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-3">
          A descrição será a mensagem que o segurado/cliente irá receber
        </p>
        {loadingCancelReasons ? (
          <Text size="sm" color="muted">
            Carregando motivos...
          </Text>
        ) : cancelReasons.length === 0 ? (
          <Text size="sm" color="muted">
            Nenhum motivo cadastrado
          </Text>
        ) : (
          <div className="space-y-2" role="radiogroup" aria-label={reasonLabel}>
            {cancelReasons.map((reason) => {
              const selected = reason.id === cancelReasonId
              return (
                <label
                  key={reason.id}
                  className={`flex items-start gap-3 rounded-lg border px-3 py-3 cursor-pointer transition-colors ${
                    selected
                      ? 'border-primary-500 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                  } ${cancelling ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  <input
                    type="radio"
                    name="agenda-cancel-reason"
                    value={reason.id}
                    checked={selected}
                    disabled={cancelling}
                    onChange={() => onReasonChange(reason.id)}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      {reason.name}
                    </span>
                    <span className="mt-1 block text-sm text-gray-600 dark:text-gray-300">
                      {reason.description}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        )}
        {cancelling && (
          <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-800 px-3 py-2 text-sm text-primary-800 dark:text-primary-200">
            {pendingMessage}
          </div>
        )}
        {cancelError && (
          <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
            {cancelError}
          </div>
        )}
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="ghost" onClick={onClose} disabled={cancelling}>
            Voltar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={
              cancelling || loadingCancelReasons || !cancelReasonId || !cancellationChannel
            }
          >
            {cancelling ? confirmingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
